import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FileClock,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  User,
} from "lucide-react";
import { listAuditLogs, type AuditLog } from "@/api/endpoints/audit";

// Human-readable color coding — the eye scans action prefixes fast.
function actionTone(action: string): string {
  if (action.endsWith(".delete")) return "bg-red-900/40 text-red-300";
  if (action.endsWith(".create")) return "bg-emerald-900/40 text-emerald-300";
  if (action.includes("reprovision") || action.includes("update"))
    return "bg-blue-900/40 text-blue-300";
  return "bg-gray-800 text-gray-300";
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  } catch {
    return iso;
  }
}

export default function AdminAudit() {
  const [filter, setFilter] = useState("");
  const [tenantId, setTenantId] = useState<string>("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);
  const pageSize = 50;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["audit", filter, tenantId, page],
    queryFn: () =>
      listAuditLogs({
        action_prefix: filter || undefined,
        tenant_id: tenantId ? Number(tenantId) : undefined,
        limit: pageSize,
        offset: page * pageSize,
      }),
    refetchInterval: 30_000,
  });

  const rows: AuditLog[] = data ?? [];
  const canNext = rows.length === pageSize;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <FileClock className="h-7 w-7 text-blue-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Audit log</h1>
          <p className="text-sm text-gray-400">
            Trace des actions admin — creations, re-provisions, suppressions.
          </p>
        </div>
      </div>

      {/* Filters */}
      <section className="rounded-lg border border-gray-800 bg-gray-900 p-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Filter className="h-4 w-4 text-gray-500" />
          <label className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Action prefix</span>
            <input
              type="text"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(0);
              }}
              placeholder="tenant. | user.create | ..."
              className="w-48 rounded border border-gray-700 bg-gray-800 px-2 py-1 font-mono text-xs text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Tenant id</span>
            <input
              type="number"
              value={tenantId}
              onChange={(e) => {
                setTenantId(e.target.value);
                setPage(0);
              }}
              placeholder="e.g. 1"
              className="w-20 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          </label>
          <span className="ml-auto text-xs text-gray-500">
            {isFetching ? "refreshing..." : `page ${page + 1}`}
          </span>
        </div>
      </section>

      {error && (
        <div className="flex items-center gap-2 rounded border border-red-700/40 bg-red-900/20 p-3 text-sm text-red-300">
          <AlertTriangle className="h-4 w-4" />{" "}
          {(error as Error).message}
        </div>
      )}

      {/* Table */}
      <section className="overflow-x-auto rounded-lg border border-gray-800 bg-gray-900">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2 font-medium">Timestamp</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Acteur</th>
              <th className="px-3 py-2 font-medium">Ressource</th>
              <th className="px-3 py-2 font-medium">Tenant</th>
              <th className="px-3 py-2 font-medium">IP</th>
              <th className="px-3 py-2 font-medium w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-500">
                  Chargement...
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-500">
                  Aucune entree.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const isExpanded = expanded === r.id;
              return (
                <>
                  <tr
                    key={r.id}
                    className="cursor-pointer hover:bg-gray-900/50"
                    onClick={() => setExpanded(isExpanded ? null : r.id)}
                  >
                    <td className="px-3 py-2 font-mono text-xs text-gray-400">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-2 py-0.5 font-mono text-[11px] ${actionTone(r.action)}`}
                      >
                        {r.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.actor_email ? (
                        <span className="flex items-center gap-1 text-gray-300">
                          <User className="h-3 w-3 text-gray-500" />
                          <span className="truncate">{r.actor_email}</span>
                        </span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                      {r.actor_role && (
                        <span className="ml-1 text-[10px] text-gray-500">
                          ({r.actor_role})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-400">
                      {r.resource_type}
                      {r.resource_id && (
                        <span className="text-gray-500">#{r.resource_id}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-400">
                      {r.tenant_id ?? "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-gray-500">
                      {r.ip_address ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-gray-950/50">
                      <td colSpan={7} className="px-3 py-3">
                        <div className="space-y-2 text-xs">
                          {r.user_agent && (
                            <div>
                              <span className="text-gray-500">UA: </span>
                              <span className="font-mono text-gray-400">
                                {r.user_agent}
                              </span>
                            </div>
                          )}
                          {r.details && Object.keys(r.details).length > 0 && (
                            <div>
                              <div className="mb-1 text-gray-500">Details:</div>
                              <pre className="overflow-x-auto rounded bg-gray-900 p-2 font-mono text-[11px] text-gray-300">
                                {JSON.stringify(r.details, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* Pagination */}
      <div className="flex items-center justify-end gap-2 text-sm">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className="inline-flex items-center gap-1 rounded border border-gray-700 bg-gray-900 px-3 py-1 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
        >
          <ChevronLeft className="h-3 w-3" />
          Precedent
        </button>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={!canNext}
          className="inline-flex items-center gap-1 rounded border border-gray-700 bg-gray-900 px-3 py-1 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
        >
          Suivant
          <ChevronRight className="h-3 w-3" />
        </button>
        <button
          onClick={() => refetch()}
          className="ml-2 rounded border border-gray-700 bg-gray-900 px-3 py-1 text-xs text-gray-300 hover:bg-gray-800"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
